/**
 * One Actual slot (start or finish) — actuals ONLY, always green. The picker
 * is capped at the data date (an actual can't be in the future), with the
 * one-click "as planned" pill beside it. The pill retires when the effective
 * plan sits past the data date (a re-planned side has no actual to record);
 * a custom staged date gets its own ↩ undo. Red = genuinely past due.
 */
import React from "react";
import { formatDateMonthDay } from "../../../../../utils/format";
import "./ActualCell.css";

interface Props {
  /** Locally staged actual (YYYY-MM-DD), or undefined. */
  staged?: string;
  /** Real committed actual from the server (iso10), or "". */
  committed: string;
  /** Effective planned date for this side (staged plan or committed), iso10. */
  planned: string;
  /** This side is genuinely past due (effective plan ≤ data date, no actual). */
  late: boolean;
  dataDate: string;
  disabled: boolean;
  /** "start" | "finish" — for titles. */
  label: string;
  onStage: (date: string) => void;
  onUnstage: () => void;
}

const ActualCell: React.FC<Props> = ({
  staged,
  committed,
  planned,
  late,
  dataDate,
  disabled,
  label,
  onStage,
  onUnstage,
}) => {
  const value = staged ?? committed;
  const isCommitted = committed !== "" && staged === undefined;
  const canAsPlanned = planned !== "" && planned <= dataDate;
  const atPlanned = staged !== undefined && staged === planned;
  const cls = value ? " set" : late ? " late" : "";
  const title = isCommitted
    ? `Actual ${label}`
    : staged !== undefined
      ? `Staged actual ${label} — applies when you proceed`
      : late
        ? `Past due — record the actual ${label}, or re-plan it`
        : `Pick the actual ${label} (on/before the data date)`;

  return (
    <span className="srm-actual">
      <input
        type="date"
        className={`srm-dateinput${cls}`}
        value={value}
        max={dataDate}
        disabled={disabled}
        title={title}
        onChange={(e) => { if (e.target.value) onStage(e.target.value); }}
      />
      {!isCommitted && (
        staged !== undefined ? (
          <button
            type="button"
            className="oap-pill oap-on"
            disabled={disabled}
            title={`Undo — clear this ${atPlanned ? "" : "staged "}${label}`}
            onClick={onUnstage}
          >{atPlanned ? "✓ as planned ↩" : "↩"}</button>
        ) : (
          <button
            type="button"
            className={`oap-pill${canAsPlanned ? "" : " oap-ghost"}`}
            disabled={disabled || !canAsPlanned}
            title={`Mark this ${label} as happening on the planned date (${formatDateMonthDay(planned)})`}
            onClick={() => onStage(planned)}
          >as planned</button>
        )
      )}
    </span>
  );
};

export default ActualCell;
